const TRACKED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

type TouchParams = Record<string, string>;

/**
 * Reports a marketing touch to api/v1/attribution/touch, which merges it and writes the
 * `utm` and `google_ads_click` cookies. The cookies are set by the response rather than
 * through document.cookie, because Safari caps a script written cookie at 7 days, or at
 * 24 hours after a landing through a click id link. The merge rules live in
 * modules/extension/helpers/AttributionHelper.php. See WBHB-10141.
 */
const readTrackedParams = (): TouchParams => {
  const params = new URLSearchParams(window.location.search);
  const tracked: TouchParams = {};

  TRACKED_KEYS.forEach((key) => {
    const value = params.get(key)?.trim();

    if (value) {
      tracked[key] = value;
    }
  });

  return tracked;
};

const registrableDomain = (hostname: string): string => hostname.toLowerCase().split('.').slice(-2).join('.');

const hasExternalReferrer = (): boolean => {
  if (!document.referrer) {
    return false;
  }

  try {
    const referrerHost = new URL(document.referrer).hostname;

    return referrerHost !== '' && registrableDomain(referrerHost) !== registrableDomain(window.location.hostname);
  } catch (error) {
    return false;
  }
};

export const saveUtmData = () => {
  const params = readTrackedParams();

  // Only a landing can carry a touch. Skipping every other page view keeps the endpoint
  // off the path of ordinary navigation between our own pages.
  if (Object.keys(params).length === 0 && !hasExternalReferrer()) {
    return;
  }

  fetch(`${document.body.dataset.apiBaseUrl ?? '/'}api/v1/attribution/touch`, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params, referrer: document.referrer }),
  }).catch(() => {
    // Attribution is best effort, and a failed touch must never surface to the visitor.
  });
};
